import { POLLY_API_URL, POLLY_API_KEY } from "../constants";

// Internal shape kept for backward-compat with buildHeaders / buildUrl below.
const API_CONFIG = {
  POLLY_API_URL,
  POLLY_API_KEY,
  TIMEOUT_MS: 15000,
};

// ─── Read Bearer token from localStorage (stored by oidc-client-ts) ───────────
//
// Strategy (in order):
//  1. Try exact OIDC key built from env vars → prefer id_token (Cognito
//     User Pools Authorizer validates the ID token), fall back to access_token
//  2. If env vars are missing / key not found, scan ALL localStorage keys
//     for any oidc.user:* entry and pick the first valid token found
// ─────────────────────────────────────────────────────────────────────────────
export const getAuthToken = () => {
  try {
    const authority = process.env.REACT_APP_COGNITO_AUTHORITY || "";
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || "";

    if (authority && clientId) {
      const key = `oidc.user:${authority}:${clientId}`;
      const raw = localStorage.getItem(key);
      if (raw) {
        const user = JSON.parse(raw);
        // Prefer id_token — Cognito User Pools Authorizer validates this.
        // Fall back to access_token for JWT Authorizer setups.
        const token = user?.id_token || user?.access_token || null;
        if (token) {
          // eslint-disable-next-line no-console
          // console.debug("[apiConfig] Auth token found via exact OIDC key.");
          return token;
        }
      }
    }

    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("oidc.user:")) {
        try {
          const user = JSON.parse(localStorage.getItem(k) || "");
          const token = user?.id_token || user?.access_token || null;
          if (token) {
            // eslint-disable-next-line no-console
            console.debug(`[apiConfig] Auth token found via fallback scan key: ${k}`);
            return token;
          }
        } catch {
          // skip malformed entries
        }
      }
    }

    // eslint-disable-next-line no-console
    console.warn("[apiConfig] ⚠️ No auth token found in localStorage. API calls may return 401.");
    return null;
  } catch {
    return null;
  }
};

// authToken (optional) — pass the live token from useAuth() context.
//   If omitted, falls back to getAuthToken() which reads from localStorage.
export const buildHeaders = (authToken = null, extra = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...extra,
  };
  if (API_CONFIG.POLLY_API_KEY) {
    headers["x-api-key"] = API_CONFIG.POLLY_API_KEY;
  }
  // Prefer the directly-supplied live token; fall back to localStorage scan.
  const token = authToken || getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  } else {
    // eslint-disable-next-line no-console
    console.warn(
      "[apiConfig] ⚠️ No auth token available — request will be sent without Authorization header."
    );
  }
  return headers;
};

export const buildUrl = (path = "", params = {}) => {
  if (!API_CONFIG.POLLY_API_URL) {
    throw new Error("REACT_APP_POLLY_API_URL is not set in .env. Please add your API Gateway URL.");
  }
  const base = API_CONFIG.POLLY_API_URL.replace(/\/$/, ""); // remove trailing slash
  const url = new URL(`${base}${path}`);
  Object.entries(params).forEach(([k, v]) => url.searchParams.append(k, v));
  return url.toString();
};

export default API_CONFIG;
